import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PiCheckCircleDuotone, PiDoorOpenDuotone, PiSpinnerGap, PiWarningDuotone } from 'react-icons/pi';
import { getInvitePreview, joinByInviteCode } from '../api/inviteLinkService';
import Button from '../shared/common/Button';
import Card from '../shared/common/Card';

const ALREADY_MEMBER_REDIRECT_DELAY_MS = 1800;

/**
 * Страница вступления в комнату по инвайт-коду.
 * URL: /invite/:code
 */
export default function JoinByInvitePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | preview | joining | success | alreadyMember | error
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);

  const navigateToRoom = useCallback((roomId, options = {}) => {
    if (roomId) {
      navigate(`/rooms/${roomId}/leagues`, options);
      return;
    }

    navigate('/rooms', options);
  }, [navigate]);

  useEffect(() => {
    document.title = 'BetMate — Приглашение';
    let redirectTimer;

    getInvitePreview(code)
      .then((data) => {
        setPreview(data);
        if (data.alreadyMember) {
          setStatus('alreadyMember');
          setMessage('Вы уже состоите в этой комнате. Сейчас перенаправим вас в комнату.');
          redirectTimer = setTimeout(
            () => navigateToRoom(data.roomId, { replace: true }),
            ALREADY_MEMBER_REDIRECT_DELAY_MS,
          );
          return;
        }

        setStatus('preview');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(inviteErrorMessage(err));
      });

    return () => clearTimeout(redirectTimer);
  }, [code, navigateToRoom]);

  const handleJoin = async () => {
    setStatus('joining');
    setMessage('');
    try {
      const member = await joinByInviteCode(code);
      const targetRoomId = member.roomId || preview?.roomId;
      setPreview((prev) => prev || { roomId: targetRoomId });
      setStatus('success');
      setMessage(`Вы вступили в комнату как ${member.role}`);
    } catch (err) {
      if (err?.response?.status === 409) {
        setStatus('alreadyMember');
        setMessage('Вы уже состоите в этой комнате. Сейчас перенаправим вас в комнату.');
        const targetRoomId = preview?.roomId;
        setTimeout(() => navigateToRoom(targetRoomId, { replace: true }), ALREADY_MEMBER_REDIRECT_DELAY_MS);
        return;
      }

      setStatus('error');
      setMessage(inviteErrorMessage(err));
    }
  };

  return (
    <div
      className="bm-animate-in"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        padding: 'var(--bm-space-xl)',
      }}
    >
      <Card style={{ maxWidth: 420, width: '100%', textAlign: 'center', padding: 'var(--bm-space-2xl)' }}>
        {(status === 'loading' || status === 'joining') && (
          <>
            <PiSpinnerGap size={48} className="bm-spin" style={{ color: 'var(--bm-accent)', marginBottom: 16 }} />
            <h2 className="bm-h3">{status === 'joining' ? 'Вступаем...' : 'Загружаем приглашение...'}</h2>
            <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 8 }}>
              {status === 'joining' ? 'Подождите, мы добавляем вас в комнату' : 'Проверяем ссылку и данные комнаты'}
            </p>
          </>
        )}

        {status === 'preview' && (
          <>
            <PiDoorOpenDuotone size={48} style={{ color: 'var(--bm-accent)', marginBottom: 16 }} />
            <h2 className="bm-h3">Приглашение в комнату</h2>
            <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 8 }}>
              Вас пригласили в комнату «{preview?.roomName || 'Без названия'}»
            </p>
            {preview?.roomDescription && (
              <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 8 }}>{preview.roomDescription}</p>
            )}
            <Button
              variant="primary"
              onClick={handleJoin}
              style={{ marginTop: 24 }}
            >
              Вступить
            </Button>
          </>
        )}

        {status === 'success' && (
          <>
            <PiCheckCircleDuotone size={48} style={{ color: 'var(--bm-accent-green)', marginBottom: 16 }} />
            <h2 className="bm-h3">Добро пожаловать!</h2>
            <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 8 }}>{message}</p>
            <Button
              variant="primary"
              onClick={() => navigateToRoom(preview?.roomId)}
              style={{ marginTop: 24 }}
            >
              Перейти в комнату
            </Button>
          </>
        )}

        {status === 'alreadyMember' && (
          <>
            <PiCheckCircleDuotone size={48} style={{ color: 'var(--bm-accent-green)', marginBottom: 16 }} />
            <h2 className="bm-h3">Вы уже участник</h2>
            <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 8 }}>{message}</p>
            <Button
              variant="primary"
              onClick={() => navigateToRoom(preview?.roomId, { replace: true })}
              style={{ marginTop: 24 }}
            >
              Перейти в комнату
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <PiWarningDuotone size={48} style={{ color: 'var(--bm-accent-red)', marginBottom: 16 }} />
            <h2 className="bm-h3">Не удалось открыть приглашение</h2>
            <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 8 }}>{message}</p>
            <Button
              variant="secondary"
              onClick={() => navigate('/rooms')}
              style={{ marginTop: 24 }}
            >
              К комнатам
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}

function inviteErrorMessage(err) {
  const msg = err?.response?.data?.error || err?.response?.data?.message;
  if (err?.response?.status === 404) {
    return 'Ссылка не найдена или недействительна';
  }

  if (err?.response?.status === 409) {
    return msg || 'Ссылка истекла или больше недоступна';
  }

  return msg || 'Не удалось открыть приглашение';
}
